import { request } from "$/lib/api"
import {
  CreateServiceWash,
  Route,
  ServiceWash,
  UpdateServiceWash,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

interface WashFilters extends Partial<ServiceWash> {
  includedIds?: string[]
  storeId: string
}

async function fetchWashes(filters: WashFilters): Promise<ServiceWash[]> {
  const { storeId, includedIds } = filters

  return await request<ServiceWash[]>({
    url: populateRoute(
      Route.SERVICE_JOB_WASH_CONTROLLER__FIND_MANY_BY_STORE_ID,
      storeId,
    ),
    params: {
      includedIds,
    },
    method: "GET",
  })
}

async function fetchWash(id: string): Promise<ServiceWash | null> {
  return await request<ServiceWash | null>({
    url: populateRoute(Route.SERVICE_JOB_WASH_CONTROLLER__FIND_ONE_BY_ID, id),
    method: "GET",
  })
}

async function createWash(data: CreateServiceWash, storeId: string) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_WASH_CONTROLLER__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function deleteWash(id: string) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_WASH_CONTROLLER__DELETE, id),
    method: "DELETE",
  })
}

async function updateWash(data: UpdateServiceWash, id: string) {
  return await request<void>({
    url: populateRoute(Route.SERVICE_JOB_WASH_CONTROLLER__UPDATE, id),
    method: "PATCH",
    data,
  })
}

export const useWashMutations = (storeId: string) => {
  const queryClient = useQueryClient()

  const invalidateQueries = async () =>
    await queryClient.invalidateQueries(washQueries.list({ storeId }))

  const createMutation = useMutation({
    mutationFn: (data: CreateServiceWash) => createWash(data, storeId),
    onSettled: invalidateQueries,
  })

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => {
      const promises = ids.map((id) => deleteWash(id))
      return Promise.all(promises)
    },
    onSettled: invalidateQueries,
  })

  const updateMutation = useMutation({
    mutationFn: ({ data, id }: { data: UpdateServiceWash; id: string }) =>
      updateWash(data, id),
    onSettled: invalidateQueries,
  })

  return { createMutation, deleteMutation, updateMutation }
}

const washQueries = {
  all: () => ["wash"],
  lists: () => [...washQueries.all(), "list"],
  list: (filters: WashFilters) =>
    queryOptions({
      queryKey: [...washQueries.lists(), filters],
      queryFn: () => fetchWashes(filters),
    }),
  details: () => [...washQueries.all(), "detail"],
  detail: (id: string) =>
    queryOptions({
      queryKey: [...washQueries.details(), id],
      queryFn: () => fetchWash(id),
    }),
}

export default washQueries
